import React, { useRef, useEffect, useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { PolarArea } from 'react-chartjs-2';
import { renderAggregatesCentral, transformCenterData } from './utils/polarChartUtils';
import { isFedDay, averageSfrFed, sanitizeSfr } from '../../constants/constantsAnalytic';

// Registrar los elementos de Chart.js
ChartJS.register(RadialLinearScale, ArcElement, Tooltip, Legend, Title);


// Función auxiliar: dado un arreglo de { day, value: { métrica: número, … } },
// devuelve la suma acumulada para la métrica indicada.
const computeCenterSum = (valuesSummary, metric) => {
  if (!Array.isArray(valuesSummary) || valuesSummary.length === 0) return 0;
  return valuesSummary.reduce((acc, { value }) => acc + (value[metric] || 0), 0);
};


// Función auxiliar: convierte "rgb(r, g, b)" en un string "r, g, b"
const extractRgbNumbers = (rgbString) => {
  // Ejemplo: rgbString = "rgb(99, 132, 255)"
  const insideParens = rgbString.replace(/^.*?\(/, '').replace(/\).*$/, '');
  // ahora "99, 132, 255"
  return insideParens;
};


export default function PolarAreaAverageChart({
  versus = [],       // Puede venir como [{ name, values_summary }, …] o [{ name, analytic_data: { ... } }, …]
  selected = [],     // array de strings con los nombres de los centros a mostrar
  selectedMetric,    // string con el nombre de la métrica que queremos graficar
  colors,
  metrics,
  nivelSeleccionado,
  translate
}) {
  const chartRef = useRef();
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const fontSizeEjes = window.innerWidth * 0.0065;
  const tooltipFontSize = window.innerWidth * 0.007;
    // --------------------------------------------------
  // Filtrar “jaulas seleccionadas” y tomar hasta 4
  const displayedSeries = useMemo(() => {
    if (nivelSeleccionado === 'region') {
      // Aplanar todas las jaulas de cada centro seleccionado
      const processed = [];

      versus
        .filter(center => selected.includes(center.name))
        .forEach(center => {
          const cages = center.analytic_data || {};

          Object.entries(cages).forEach(([jaulaId, jaula]) => {
            const vs = jaula.values_summary;

            const values_summary = vs && typeof vs === 'object' && !Array.isArray(vs)
              ? Object.entries(vs).map(([day, value]) => ({ day, value }))
              : Array.isArray(vs) ? vs : [];

            processed.push({
              name: jaula.name || jaulaId,
              values_summary
            });
          });
        });

      return processed.slice(0, 4);
    }
    else if( nivelSeleccionado === 'area'){
      // Agrupar y acumular datos por centro
      const groupedData = transformCenterData(versus);
      // Filtrar solo los centros seleccionados y tomar hasta 4
      return groupedData.filter((center) => selected.includes(center.seriesName)).slice(0, 4);
    } else if( nivelSeleccionado === 'centro'){
      return versus
      .filter((serieObj) => selected.includes(serieObj.name))
      .slice(0, 4);
    }
    
  }, [nivelSeleccionado, versus, selected]);

  // --------------------------------------------------
  // 1) Calcular agregados por jaula (serie) para todas las métricas
  const aggregatedBySeries = useMemo(() => {
      return displayedSeries?.map((serieObj) => {
        const sums = {
          delivered_food: 0,
          alarms_count: 0,
          perdidos_count: 0,
          visitas_count: 0,
        };
        let sfrSum = 0;
        let sfrCount = 0;
  
        serieObj?.values_summary?.forEach((item) => {
          const vals = item.value;
          const df = Number(vals.delivered_food);
          if (!isNaN(df)) sums.delivered_food += df;
  
          const ac = Number(vals.alarms_count);
          if (!isNaN(ac)) sums.alarms_count += ac;
  
          const pc = Number(vals.perdidos_count );
          if (!isNaN(pc)) sums.perdidos_count += pc;
  
          const vc = Number(vals.visitas_count );
          if (!isNaN(vc)) sums.visitas_count += vc;
  
          // SFR promedio: solo días alimentados (excluye ayunos) y con SFR saneado (>1000% recalculado/descartado)
          const sv = sanitizeSfr(vals);
          if (isFedDay(vals) && Number.isFinite(sv)) { // null (SFR irrecuperable) se ignora
            sfrSum += sv;
            sfrCount += 1;
          }
        });
        // console.log('serieObj',serieObj)
        return {
          seriesName:    serieObj.name || serieObj.seriesName,
          delivered_food: sums.delivered_food,
          alarms_count:   sums.alarms_count,
          perdidos_count:  sums.perdidos_count,
          visitas_count:   sums.visitas_count,
          sfr_value:    sfrCount > 0 ? sfrSum / sfrCount : 0,
        };
      });
  }, [displayedSeries]);

  const buildValuesSummary = (centerObj) => {
    if (Array.isArray(centerObj.values_summary)) {
      return centerObj.values_summary;
    }

    if (centerObj.analytic_data && typeof centerObj.analytic_data === 'object') {
      const firstItem = Object.values(centerObj.analytic_data)?.[0];
      const isFlat = firstItem?.values_summary || firstItem?.tasa_summary;

      if (isFlat) {
        return Object.values(centerObj.analytic_data).flatMap((cage) => {
          const vs = cage.values_summary;

          // Transformar el objeto values_summary en array de { day, value }
          if (vs && typeof vs === 'object' && !Array.isArray(vs)) {
            return Object.entries(vs).map(([day, value]) => ({ day, value }));
          }

          return Array.isArray(vs) ? vs : [];
        });
      } else {
        // Caso con zona intermedia (zona → jaula → values_summary)
        return Object.values(centerObj.analytic_data).flatMap((zona) =>
          Object.values(zona).flatMap((cage) => {
            const vs = cage.values_summary;
            if (vs && typeof vs === 'object' && !Array.isArray(vs)) {
              return Object.entries(vs).map(([day, value]) => ({ day, value }));
            }
            return Array.isArray(vs) ? vs : [];
          })
        );
      }
    }

    return [];
  };

  useEffect(() => {


    // 2) Filtramos primero por `selected`, luego extraemos summary y nos quedamos
    //    sólo con los que tengan al menos un día con datos.
    const validCenters = versus
      .filter((center) => selected.includes(center.name))
      .map((center) => {
        const summaryArray = buildValuesSummary(center);
        return {
          name: center.name,
          values_summary: summaryArray,
        };
      })
      .filter(
        (c) => Array.isArray(c.values_summary) && c.values_summary.length > 0
      );

    // Si no hay ningún centro válido, limpiamos el gráfico y salimos
    if (!validCenters.length) {
      const emptyData = { labels: [], datasets: [] };
      if (JSON.stringify(emptyData) !== JSON.stringify(chartData)) {
        setChartData(emptyData);
      }
      return;
    }

    // 3) Armar etiquetas (nombres) y valores (promedios) para cada centro filtrado
    const labels = [];
    const dataValues = [];

    // validCenters.forEach((center) => {
    //   const { name, values_summary: summary } = center;
    //   labels.push(name);
    //   const avg = computeCenterAverage(summary, selectedMetric);
    //   dataValues.push(avg);
    // });
     validCenters.forEach(({ name, values_summary }) => {
      labels.push(name);
      // Si la métrica es 'sfr', mostrar promedio (solo días alimentados), si no, acumulado
      const value = selectedMetric === 'sfr_value'
        ? averageSfrFed(values_summary)
        : computeCenterSum(values_summary, selectedMetric);
      dataValues.push(value);
    });
    const metricLabel = metrics.find((m) => m.key === selectedMetric)?.label || selectedMetric;
    // 4) Construir el objeto que Chart.js consume
    const newChartData = {
      labels,
      datasets: [
        {
          label: selectedMetric === 'sfr_value' ? `Promedio de ${metricLabel} [ % ]` : `Acumulado de ${metricLabel}`,
          //`Promedio de ${selectedMetric}`,
          data: dataValues,
          // backgroundColor: colors,//labels.map( (_, i) =>   `rgba(${[255, 99, 132, 159, 205][i % 5]}, ${     [99, 159, 205, 192, 162][i % 5]   }, ${[132, 64, 86, 192, 235][i % 5]}, 0.5)`),
          backgroundColor: labels.map( (_, i) =>  {
            const baseRgb = extractRgbNumbers(colors[i % colors.length]);
            return( `rgba(${baseRgb}, 0.5)`)
          }),
          borderColor: labels.map(() => 'transparent'),
          borderWidth: 0,
        },
      ],
    };

    // 5) Solo actualizar si cambió algo para evitar bucles infinitos
    if (JSON.stringify(newChartData) !== JSON.stringify(chartData)) {
      setChartData(newChartData);
    }
  }, [versus, selected, selectedMetric, chartData]);

  const options = {
    responsive: true,
    scales: {
      r: {
        grid: {
          color: 'rgba(255, 255, 255, 0.29)',
        },
        angleLines: {
          color: 'rgba(255,255,255, 0.29)',
        },
        pointLabels: {
          display: true,
          centerPointLabels: true,
          font: { size: fontSizeEjes },
          color: '#c3c3c3',
        },
        ticks: {
          backdropColor: 'transparent',
          color: '#c3c3c3',
          padding: 8,
        },
      },
    },
    plugins: {
      legend: { position: 'top', font: { size: fontSizeEjes }, color: '#c3c3c3' },
      title: {
        display: false,
        text: 'Promedios',
      },
      tooltip: {
        enabled: true,
        mode: 'dataset',      // ← muestra todos los elementos del mismo dataset
        intersect: false,     // ← no requiere acercarse exactamente al punto
        titleColor: '#c3c3c3',
        bodyColor: '#c3c3c3',
        borderColor: '#a3a3a3',
        borderWidth: 1,
        bodyFont: {
          size: tooltipFontSize,  // ← aquí usas vw convertido a px
        },
        titleFont: {
          size: tooltipFontSize * 1.1, // un poco más grande para el título
        },
        // callbacks: {
        // label: (ctx) => {
        //   // cambio aquí: ctx.raw en vez de ctx.parsed
        //   const v = ctx.raw;
        //   return `${ctx.label}: ${v.toFixed(2)}`;
        // }
      // },
      },
    },
  };

   const renderAggregatesCentralProps = {colors,aggregatedBySeries, nivelSeleccionado, translate}

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignContent:'center', alignItems:'center', height: '60vh', flexDirection:'column' }}>
      <div
        style={{
          width: '33vw',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          flexDirection: 'row',
          // marginTop: "5vh",
          height:'14vh',
          // border:'solid 0.01vw'
        }}
      >
        {renderAggregatesCentral(renderAggregatesCentralProps)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignContent:'center', alignItems:'center', height: '46vh', width:'100%', flexDirection:'column', 

      }}>
        <PolarArea ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
}
